import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { DataService, AuthService, LayoutService } from '../../services';
import { UserCredentials, AuthenticateReturn, MfaVerifyRequest } from '../../models';
import { UserMode } from '../../models/user';

/**
 * Custom validator to ensure password contains at least one letter and one number
 */
function passwordComplexityValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) {
    return null;
  }
  
  const hasLetter = /[a-zA-Z]/.test(value);
  const hasNumber = /[0-9]/.test(value);
  
  if (!hasLetter || !hasNumber) {
    return { passwordComplexity: true };
  }
  
  return null;
}

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  forgotPasswordForm: FormGroup;
  passwordResetForm: FormGroup;
  mfaForm: FormGroup;
  hidePassword = true;
  hideNewPassword = true;
  hideConfirmPassword = true;
  isLoggingIn = false;
  isSendingResetEmail = false;
  isValidatingCode = false;
  isResettingPassword = false;
  isVerifyingMfa = false;
  forgotPasswordMode = false;
  passwordResetMode = false;
  mfaMode = false;
  resetCodeValid = false;
  returnUrl: string = '/dashboard'; // Default return URL
  resetCode: string = '';
  resetEmail: string = '';
  mfaUsername: string = '';

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private dataService: DataService,
    private authService: AuthService,
    private layoutService: LayoutService,
    private snackBar: MatSnackBar
  ) {
    this.loginForm = this.fb.group({
      username: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]]
    });

    this.forgotPasswordForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });

    this.passwordResetForm = this.fb.group({
      newPassword: ['', [Validators.required, Validators.minLength(8), passwordComplexityValidator]],
      confirmPassword: ['', [Validators.required]]
    });

    this.mfaForm = this.fb.group({
      code: ['', [Validators.required, Validators.minLength(6)]]
    });

    // Get return URL from route parameters or default to '/dashboard'
    const returnUrlParam = this.route.snapshot.queryParams['returnUrl'];
    // Sanitize return URL - ensure it's not empty, not just '/', and is a valid path
    this.returnUrl = this.sanitizeReturnUrl(returnUrlParam);
  }

  ngOnInit(): void {
    // Check if we're in password reset mode
    const code = this.route.snapshot.queryParams['code'];
    const email = this.route.snapshot.queryParams['email'];
    
    if (code && email) {
      // Password reset mode - allow even if logged in
      this.resetCode = code;
      this.resetEmail = email;
      this.passwordResetMode = true;
      this.validateResetCode(code);
    } else if (this.authService.isAuthenticated()) {
      // If user is already logged in and not in password reset mode, redirect them
      const destination = this.sanitizeReturnUrl(this.returnUrl);
      this.router.navigate([destination]);
    }
  }

  onSubmit() {
    if (this.loginForm.valid) {
      this.isLoggingIn = true;
      
      const credentials: UserCredentials = {
        userName: this.loginForm.get('username')?.value,
        password: this.loginForm.get('password')?.value
      };

      this.dataService.authenticate(credentials).subscribe({
        next: (result: AuthenticateReturn) => {
          // Check if MFA is required
          if (result.mfaRequired) {
            this.isLoggingIn = false;
            this.mfaMode = true;
            this.mfaUsername = credentials.userName || '';
            this.mfaForm.reset();
            return;
          }
          
          // Store authentication result (token, user info, etc.)
          if (result.token) {
            this.completeAuthentication(result);
          } else {
            this.handleLoginError('Authentication failed. Please try again.');
          }
        },
        error: (error) => {
          console.error('Authentication error:', error);
          this.handleLoginError('Login failed. Please check your credentials and try again.');
        }
      });
    }
  }

  private handleLoginError(message: string) {
    this.isLoggingIn = false;
    this.snackBar.open(message, '×', {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['error-snackbar']
    });
  }

  onMfaSubmit() {
    if (this.mfaForm.valid) {
      this.isVerifyingMfa = true;
      
      const mfaRequest: MfaVerifyRequest = {
        userName: this.mfaUsername,
        code: this.mfaForm.get('code')?.value,
        product: 'DQP'
      };

      this.dataService.mfaVerify(mfaRequest).subscribe({
        next: (response) => {
          this.isVerifyingMfa = false;
          
          if (response.success && response.authenticateReturn) {
            this.completeAuthentication(response.authenticateReturn);
          } else {
            this.snackBar.open(
              response.message || 'MFA verification failed. Please try again.',
              '×',
              {
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['error-snackbar']
              }
            );
          }
        },
        error: (error) => {
          this.isVerifyingMfa = false;
          console.error('MFA verification error:', error);
          this.snackBar.open(
            'MFA verification failed. Please check your code and try again.',
            '×',
            {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            }
          );
        }
      });
    }
  }

  cancelMfa() {
    this.mfaMode = false;
    this.mfaForm.reset();
    this.mfaUsername = '';
  }

  private completeAuthentication(result: AuthenticateReturn) {
    // Set current user in AuthService using the API response
    this.authService.setCurrentUser({
      userId: result.userId,
      username: result.userName,
      firstName: result.firstName,
      lastName: result.lastName,
      companyName: result.companyName,
      token: result.token
    });
    
    // Fetch user's plan after successful authentication
    this.dataService.getUserPlan().subscribe({
      next: (plan) => {
        this.authService.setUserPlan(plan);
      },
      error: (error) => {
        console.error('Failed to fetch user plan:', error);
        // Continue with login even if plan fetch fails
      }
    });
    
    // Fetch user's settings and set view mode
    this.dataService.getUserSettings().subscribe({
      next: (settings) => {
        this.authService.setUserSettings(settings);
        
        // Set view mode based on user settings
        const viewMode = settings.userMode === UserMode.Advanced ? 'advanced' : 'simple';
        this.layoutService.setViewMode(viewMode);
      },
      error: (error) => {
        console.error('Failed to fetch user settings:', error);
        // Default to simple mode if settings fetch fails
        this.layoutService.setViewMode('simple');
      }
    });
    
    // Trigger the animation and navigation
    this.animateAndNavigate();
  }

  animateAndNavigate() {
    // Navigate directly without expand animation
    const destination = this.sanitizeReturnUrl(this.returnUrl);
    this.router.navigate([destination]).catch(err => {
      // If navigation fails, fallback to dashboard
      console.error('Navigation failed, redirecting to dashboard:', err);
      this.router.navigate(['/dashboard']);
    }).finally(() => {
      this.isLoggingIn = false;
    });
  }

  togglePasswordVisibility() {
    this.hidePassword = !this.hidePassword;
  }

  showForgotPassword() {
    this.forgotPasswordMode = true;
    this.forgotPasswordForm.reset();
  }

  cancelForgotPassword() {
    this.forgotPasswordMode = false;
    this.forgotPasswordForm.reset();
  }

  onForgotPasswordSubmit() {
    if (this.forgotPasswordForm.valid) {
      this.isSendingResetEmail = true;
      const email = this.forgotPasswordForm.get('email')?.value;

      this.dataService.requestPasswordResetCode(email).subscribe({
        next: (success) => {
          this.isSendingResetEmail = false;
          if (success) {
            this.snackBar.open(
              'Password reset code has been sent to your email.',
              '×',
              {
                horizontalPosition: 'center',
                verticalPosition: 'top',
                duration: 5000,
                panelClass: ['success-snackbar']
              }
            );
            this.cancelForgotPassword();
          } else {
            this.snackBar.open(
              'Failed to send reset code. Please try again.',
              '×',
              {
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['error-snackbar']
              }
            );
          }
        },
        error: (error) => {
          this.isSendingResetEmail = false;
          console.error('Password reset error:', error);
          this.snackBar.open(
            'An error occurred. Please try again later.',
            '×',
            {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            }
          );
        }
      });
    }
  }

  validateResetCode(code: string) {
    this.isValidatingCode = true;
    this.dataService.validatePasswordResetCode(code, this.resetEmail).subscribe({
      next: (isValid) => {
        this.isValidatingCode = false;
        this.resetCodeValid = isValid;
        if (!isValid) {
          this.snackBar.open(
            'Invalid or expired reset code. Please request a new one.',
            '×',
            {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            }
          );
        }
      },
      error: (error) => {
        this.isValidatingCode = false;
        this.resetCodeValid = false;
        console.error('Code validation error:', error);
        this.snackBar.open(
          'Error validating reset code. Please try again.',
          '×',
          {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          }
        );
      }
    });
  }

  onPasswordResetSubmit() {
    if (this.passwordResetForm.valid) {
      const newPassword = this.passwordResetForm.get('newPassword')?.value;
      const confirmPassword = this.passwordResetForm.get('confirmPassword')?.value;

      if (newPassword !== confirmPassword) {
        this.snackBar.open(
          'Passwords do not match.',
          '×',
          {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          }
        );
        return;
      }

      this.isResettingPassword = true;
      this.dataService.resetPassword(this.resetCode, newPassword, this.resetEmail).subscribe({
        next: (success) => {
          this.isResettingPassword = false;
          if (success) {
            this.snackBar.open(
              'Password reset successful! Please log in with your new password.',
              '×',
              {
                horizontalPosition: 'center',
                verticalPosition: 'top',
                duration: 5000,
                panelClass: ['success-snackbar']
              }
            );
            // Navigate to login page
            this.router.navigate(['/login']);
          } else {
            this.snackBar.open(
              'Failed to reset password. Please try again.',
              '×',
              {
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['error-snackbar']
              }
            );
          }
        },
        error: (error) => {
          this.isResettingPassword = false;
          console.error('Password reset error:', error);
          this.snackBar.open(
            'An error occurred. Please try again later.',
            '×',
            {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            }
          );
        }
      });
    }
  }

  /**
   * Sanitize the return URL to ensure it's valid
   * @param url The return URL from query params
   * @returns A valid return URL or default to /dashboard
   */
  private sanitizeReturnUrl(url: string | null | undefined): string {
    // If no URL provided, or it's empty/whitespace, default to dashboard
    if (!url || url.trim() === '' || url.trim() === '/') {
      return '/dashboard';
    }

    // Remove leading/trailing whitespace
    url = url.trim();

    // Don't redirect back to login or password-reset pages
    if (url === '/login' || url === '/password-reset' || url.startsWith('/login') || url.startsWith('/password-reset')) {
      return '/dashboard';
    }

    // Security: Ensure it's a relative path (prevent open redirect vulnerabilities)
    // Check if it starts with '/' and doesn't contain protocol (http://, https://, //)
    if (!url.startsWith('/') || url.startsWith('//') || url.includes('://')) {
      return '/dashboard';
    }

    // Return the sanitized URL
    return url;
  }

  cancelPasswordReset() {
    this.router.navigate(['/login']);
  }

  toggleNewPasswordVisibility() {
    this.hideNewPassword = !this.hideNewPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.hideConfirmPassword = !this.hideConfirmPassword;
  }
}
